'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { formatDateTime, getCountryCodeFromLocale, getLocalizedValue } from '@/lib/utils'
import {
  MoreHorizontalIcon,
  EyeIcon,
  EditIcon,
  Trash2Icon,
  LanguagesIcon,
  KeyIcon,
  CalendarIcon,
  HashIcon,
  SquarePlusIcon,
  SquareMenuIcon,
  RefreshCwIcon
} from 'lucide-react'
import Flag from 'react-flagkit'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { I18nMessagesFilters } from '@/components/partials/i18n-messages'
import { LoadingState, SortableHeader, DeleteConfirmationDialog } from '@/components/partials'
import { I18nMessagesFilterParams } from '@/types/i18n-message'
import { useI18nMessages, useDeleteI18nMessage, useBulkDeleteI18nMessages } from '@/hooks'
import { useI18nMessagesStore, useMetaStore } from '@/store'
import { SortEnum } from '@/enums'
import { BulkDeleteResponse } from '@/types/general'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteMessageId, setDeleteMessageId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  const selectedMessageIds = useI18nMessagesStore(state => state.selectedMessageIds)
  const toggleMessageSelection = useI18nMessagesStore(state => state.toggleMessageSelection)
  const selectAllMessages = useI18nMessagesStore(state => state.selectAllMessages)
  const deselectAllMessages = useI18nMessagesStore(state => state.deselectAllMessages)

  const filters = useMemo<I18nMessagesFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'key',
      q: params.get('q') || '',
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])

  const { metaState } = useMetaStore()
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale
  const { data: messagesData, isLoading, error, refetch, isFetching } = useI18nMessages(filters)
  const { mutate: deleteMessageMutate, isPending: isDeleting } = useDeleteI18nMessage()
  const { mutate: bulkDeleteMessagesMutate, isPending: isBulkDeleting } = useBulkDeleteI18nMessages()

  // URL params handling
  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    const newFilters: I18nMessagesFilterParams = {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'key'
    }

    if (params.get('q')) {
      newFilters.q = params.get('q') || ''
    }
    if (params.get('createdAtStart')) {
      newFilters.createdAtStart = params.get('createdAtStart') || undefined
    }
    if (params.get('createdAtEnd')) {
      newFilters.createdAtEnd = params.get('createdAtEnd') || undefined
    }
    if (params.get('updatedAtStart')) {
      newFilters.updatedAtStart = params.get('updatedAtStart') || undefined
    }
    if (params.get('updatedAtEnd')) {
      newFilters.updatedAtEnd = params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<I18nMessagesFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value.toString())
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<I18nMessagesFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/localization/i18n-messages')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (messageId: string) => {
    router.push(`/localization/i18n-messages/${messageId}`)
  }

  const handleEdit = (messageId: string) => {
    router.push(`/localization/i18n-messages/${messageId}/edit`)
  }

  const handleDelete = (messageId: string) => {
    setDeleteMessageId(messageId)
  }

  const confirmDeleteMessage = () => {
    if (deleteMessageId) {
      deleteMessageMutate(deleteMessageId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['i18nMessages'] })
          toast.success(t('deleted_successfully'))
          setDeleteMessageId(null)
        },
        onError: error => {
          console.error('Error deleting i18n property:', error)
          toast.error(t('error_deleting'))
          setDeleteMessageId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedMessageIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteMessages = () => {
    if (selectedMessageIds.length > 0) {
      bulkDeleteMessagesMutate(selectedMessageIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['i18nMessages'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (messagesData && messagesData.items.length > 0) {
      if (selectedMessageIds.length === messagesData.items.length) {
        // If all messages are selected, deselect all
        deselectAllMessages()
      } else {
        // Otherwise, select all messages
        selectAllMessages(messagesData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <LanguagesIcon className="h-5 w-5" />
                {t('i18n_messages')}
              </CardTitle>
              <CardDescription>{t('i18n_messages_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              <Button asChild>
                <Link href="/localization/i18n-messages/create">
                  <SquarePlusIcon />
                  <span className="hidden items-center gap-2 lg:flex">{t('create_i18n_message')}</span>
                </Link>
              </Button>
              {selectedMessageIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedMessageIds.length})</span>
                  </div>
                </Button>
              )}
              <I18nMessagesFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {messagesData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={
                                messagesData &&
                                messagesData.items.length > 0 &&
                                selectedMessageIds.length === messagesData.items.length
                              }
                              onCheckedChange={() => {
                                handleSelectAll()
                              }}
                              onClick={e => {
                                e.stopPropagation()
                              }}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        <TableHead className="w-25">
                          <SortableHeader
                            sortBy="id"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <HashIcon className="mr-2 h-4 w-4" />
                            ID
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-75">
                          <SortableHeader
                            sortBy="key"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <KeyIcon className="mr-2 h-4 w-4" />
                            {t('key')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead>
                          <SortableHeader
                            sortBy={`translations.${locale}.value`}
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <Flag country={getCountryCodeFromLocale(locale)} size={16} />
                              {t('value')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-37.5">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-37.5">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-20">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {messagesData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        messagesData.items.map((message, index) => (
                          <ContextMenu key={message.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => handleView(message.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    <Checkbox
                                      checked={selectedMessageIds.includes(message.id)}
                                      onCheckedChange={() => toggleMessageSelection(message.id)}
                                      onClick={e => {
                                        e.stopPropagation()
                                      }}
                                      className="cursor-pointer"
                                    />
                                  </div>
                                </TableCell>
                                <TableCell className="text-muted-foreground font-mono text-xs">{message.id}</TableCell>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="font-mono text-xs">
                                      {message.key}
                                    </Badge>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="max-w-75 truncate">
                                    {getLocalizedValue(message, 'value', locale, defaultLocale)}
                                  </div>
                                </TableCell>
                                <TableCell className="text-muted-foreground text-center text-sm">
                                  {formatDateTime(message.createdAt, locale)}
                                </TableCell>
                                <TableCell className="text-muted-foreground text-center text-sm">
                                  {formatDateTime(message.updatedAt, locale)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        onClick={e => e.stopPropagation()}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          handleView(message.id)
                                        }}>
                                        <EyeIcon className="mr-2 h-4 w-4" />
                                        {t('i18n_message_details')}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          handleEdit(message.id)
                                        }}>
                                        <EditIcon className="mr-2 h-4 w-4" />
                                        {t('edit_i18n_message')}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          handleDelete(message.id)
                                        }}
                                        className="text-destructive focus:text-destructive">
                                        <Trash2Icon className="mr-2 h-4 w-4" />
                                        {t('delete')}
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>
                              <ContextMenuItem
                                onClick={e => {
                                  e.stopPropagation()
                                  handleView(message.id)
                                }}>
                                <EyeIcon className="mr-2 h-4 w-4" />
                                {t('i18n_message_details')}
                              </ContextMenuItem>
                              <ContextMenuItem
                                onClick={e => {
                                  e.stopPropagation()
                                  handleEdit(message.id)
                                }}>
                                <EditIcon className="mr-2 h-4 w-4" />
                                {t('edit_i18n_message')}
                              </ContextMenuItem>
                              <ContextMenuItem
                                onClick={e => {
                                  e.stopPropagation()
                                  handleDelete(message.id)
                                }}
                                className="text-destructive focus:text-destructive">
                                <Trash2Icon className="mr-2 h-4 w-4" />
                                {t('delete')}
                              </ContextMenuItem>
                            </ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={messagesData.page}
                  totalPages={messagesData.pages}
                  totalItems={messagesData.total}
                  pageSize={messagesData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={deleteMessageId !== null}
        onCancel={() => setDeleteMessageId(null)}
        onConfirm={confirmDeleteMessage}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationDialog
        open={bulkDeleteConfirmOpen}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDeleteMessages}
        isDeleting={isBulkDeleting}
        description={t('bulk_delete_confirmation_message')}
      />
    </>
  )
}

export default List
